import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { setSession, clearSession } from "@/lib/auth-session";

/**
 * Handles the redirect back from the backend's Google OAuth flow. On success the
 * backend redirects here with `#access_token=...&refresh_token=...`; on failure it
 * redirects straight to `/login?error=google_auth_failed` instead, so this page only
 * ever needs to handle the success shape (plus a defensive fallback for a missing hash).
 */
export default function AuthCallbackPage() {
  const [, setLocation] = useLocation();
  const { hydrateUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    async function handleCallback() {
      const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
      const params = new URLSearchParams(hash);
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");

      if (!accessToken || !refreshToken) {
        setError("No sign-in tokens found. Please try again.");
        return;
      }

      setSession({ accessToken, refreshToken });
      window.history.replaceState(null, document.title, window.location.pathname);

      const res = await authFetch(apiUrl("/auth/user"));
      if (!res.ok) {
        clearSession();
        setError("We couldn't complete your sign-in right now. Please try again in a moment.");
        return;
      }
      const data = (await res.json().catch(() => ({}))) as { user?: unknown };
      if (!data.user) {
        clearSession();
        setError("We couldn't complete your sign-in right now. Please try again in a moment.");
        return;
      }

      hydrateUser(data.user as Parameters<typeof hydrateUser>[0]);
      setLocation("/");
    }

    void handleCallback();
  }, [setLocation, hydrateUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(40_30%_97%)] flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3">
            {error}
          </div>
          <a
            href="/login"
            className="inline-block text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(40_30%_97%)] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 rounded-full border-2 border-[hsl(258_35%_50%)] border-t-transparent animate-spin" />
        <p className="text-sm text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
